import React, { useState } from 'react'
import PropTypes from 'prop-types'
import classNames from 'classnames'
import { Logo, Box, Icon, Text } from '@deseretbook/react-ui'
import { getUser, isAuthenticated } from '../api/users'
import { Container, Navbar, SimpleSidebar, Menu, Hero } from '../dbWebUI'
import { libraryUrl, loginUrl, logoutUrl } from '../utils/routes'
import SearchBar from './search/SearchBar'
import { useMediaQuery } from '@deseretbook/react-utils'

/**
 * Main header component.
 */
const MainHeader = ({ className, isFixed, hasScrollToggle, navbarProps, ...rest }) => {
  const [isOpen, setIsOpen] = useState(false)
  const isDesktopOrLarger = useMediaQuery({ minWidth: 1025 })
  const isTabletOrSmaller = useMediaQuery({ maxWidth: 830 })

  /**
   * Toggles the visibility of the menu on small sized screens.
   *
   * @param {boolean} [isOpen=true] - If the menu should be open or closed
   */
  const toggleMenu = (newIsOpen = true) => {
    setIsOpen(newIsOpen)
  }
  const classes = classNames('MainHeader', className)
  const dropdownClasses = classNames('outside-link')
  const pathname = window.location.pathname
  const isLibrary = pathname.includes('/library')
  const isDiscover = pathname === '/' || pathname.includes('/discover')

  const legalLinks = [
    {
      link: 'https://support.deseretbook.com/article/5-contact-deseret-book',
      name: 'Contact Us',
    },
    { link: 'https://support.deseretbook.com/', name: 'Questions & Support' },
    { link: 'https://www.deseretbook.com/terms-of-use.html', name: 'Terms of Use' },
    { link: 'https://www.deseretbook.com/privacy.html', name: 'Privacy Policy' },
    { link: 'https://www.deseretbook.com/legal.html', name: 'Legal' },
  ]

  const legalLinksSidebar = (
    <>
      {legalLinks.map((link) => (
        <Menu.ListItem>
          <a className="sidebar-link" href={link.link}>
            {link.name}
          </a>
        </Menu.ListItem>
      ))}
    </>
  )

  // Mobile sidebar menu
  const sidebar = (
    <SimpleSidebar isOpen={isOpen} onClose={() => toggleMenu(false)}>
      <SimpleSidebar.Content>
        <Hero style={{ backgroundColor: '#f0f0f0' }}>
          <Hero.Body hasTextCentered>
            <Box marginRight={0.5} alignItems="center" isColumn>
              <Logo
                style={{ maxWidth: '48px', maxHeight: '48px', marginBottom: '1rem' }}
                name="bookshelfBooks"
                alt="Deseret Bookshelf Books"
              />
            </Box>
            <Logo
              style={{ minWidth: '181px', minHeight: '16px' }}
              name="bookshelfTitle"
              alt="Deseret Bookshelf Title"
              svgProps={{ width: '181', height: '16' }}
            />
          </Hero.Body>
        </Hero>
        <Menu>
          <Menu.List>
            {isAuthenticated() && isTabletOrSmaller ? (
              <Menu.ListItem style={{ padding: '0.5rem 0.25rem' }}>
                <SearchBar
                  sidebar
                  searchCallback={() => {
                    toggleMenu(false)
                  }}
                />
              </Menu.ListItem>
            ) : null}
            <Menu.ListItem>
              <Menu.ListItem.Link className="sidebar-link" to={'/'}>
                Discover
              </Menu.ListItem.Link>
            </Menu.ListItem>
            <Menu.ListItem>
              <Menu.ListItem.Link className="sidebar-link" to={libraryUrl()}>
                Library
              </Menu.ListItem.Link>
            </Menu.ListItem>
            {legalLinksSidebar}
            <Menu.ListItem>
              {!isAuthenticated() ? (
                <Menu.ListItem.Link className="sidebar-link" to={loginUrl()}>
                  Sign in
                </Menu.ListItem.Link>
              ) : (
                <Menu.ListItem.Link
                  className="sidebar-link"
                  style={{ color: '#C93644' }}
                  to={logoutUrl()}
                >
                  Sign out
                </Menu.ListItem.Link>
              )}
            </Menu.ListItem>
          </Menu.List>
        </Menu>
      </SimpleSidebar.Content>
    </SimpleSidebar>
  )

  const legalLinksNavbar = (
    <>
      {legalLinks.map((link) => (
        <Navbar.OutsideLink
          className={dropdownClasses}
          target="_blank"
          rel="noopener noreferrer"
          href={link.link}
        >
          {link.name}
        </Navbar.OutsideLink>
      ))}
    </>
  )

  return (
    <header className={classes} {...rest}>
      <Navbar
        isFixedTop={isFixed}
        hasScrollToggle={hasScrollToggle}
        hasShadow
        isPrimary
        {...navbarProps}
      >
        <Container style={{ display: 'flex' }}>
          <Navbar.Brand>
            <Navbar.Link to={'/'} activeClassName="">
              <Box marginRight={0.5}>
                <Logo
                  style={{ width: '48px', height: '48px' }}
                  name="bookshelfBooks"
                  alt="Deseret Bookshelf Books"
                  svgProps={{ width: '48', height: '48' }}
                />
              </Box>
              <Logo
                style={{ minWidth: '181px', maxWidth: '181px', minHeight: '16px' }}
                name="bookshelfTitle"
                alt="Deseret Bookshelf Title"
                svgProps={{ width: '181', height: '16' }}
              />
            </Navbar.Link>
            <Navbar.Link
              to={'/'}
              style={{
                marginRight: '8px',
                backgroundColor: 'transparent',
                borderBottom: `2px solid ${isDiscover ? '#0F8079' : 'transparent'}`,
              }}
            >
              <Box alignItems="center">
                <Icon
                  name="compass"
                  style={{ marginRight: '8px', height: '24px', width: '24px' }}
                />
                <Text
                  style={{
                    fontFamily: 'Roboto, sans-serif',
                    fontWeight: 700,
                  }}
                >
                  Discover
                </Text>
              </Box>
            </Navbar.Link>
            <Navbar.Link
              to={'/library'}
              style={{
                backgroundColor: 'transparent',
                borderBottom: `2px solid ${isLibrary ? '#0F8079' : 'transparent'}`,
              }}
            >
              <Box alignItems="center">
                <Icon
                  name="library"
                  style={{ marginRight: '8px', height: '22px', width: '22px' }}
                  svgProps={{ height: '22', width: '22' }}
                />
                <Text
                  style={{
                    fontFamily: 'Roboto, sans-serif',
                    fontWeight: 700,
                  }}
                >
                  Library
                </Text>
              </Box>
            </Navbar.Link>
            <Navbar.Burger isActive={isOpen} onClick={toggleMenu} />
          </Navbar.Brand>
          <Navbar.End style={{ width: '100%', display: 'flex-end', paddingTop: '0.25rem' }}>
            {isAuthenticated() ? (
              <Navbar.StaticItem
                style={{
                  alignSelf: 'center',
                  height: '100%',
                  display: isTabletOrSmaller ? 'none' : 'inherit',
                }}
              >
                <SearchBar />
              </Navbar.StaticItem>
            ) : null}
            {!isAuthenticated() ? (
              <>
                <Navbar.Link
                  to={loginUrl()}
                  style={{
                    fontFamily: 'Roboto, sans-serif',
                    color: 'black',
                    backgroundColor: '#f0f0f0',
                  }}
                >
                  Sign in
                </Navbar.Link>
              </>
            ) : (
              <Navbar.Dropdown
                isHoverable
                id="profile"
                style={{ display: isDesktopOrLarger ? 'flex' : 'none' }}
              >
                <Navbar.Dropdown
                  style={{
                    width: '15.5rem',
                    justifyContent: 'center',
                  }}
                >
                  <Box alignItems="center" display="flex">
                    <Icon
                      name="profile"
                      style={{ marginRight: '0.5rem' }}
                      svgProps={{ height: '30' }}
                    />
                    <Text
                      style={{
                        fontFamily: 'Roboto, sans-serif',
                        fontWeight: 700,
                        fontSize: '16px',
                        lineHeight: '18.75px',
                      }}
                    >
                      {getUser().name}
                    </Text>
                    <Icon name="chevronUp" style={{ marginLeft: '0.5rem', color: 'black' }} />
                  </Box>
                </Navbar.Dropdown>
                <Navbar.Dropdown.Menu isRight>
                  <Navbar.OutsideLink
                    className={dropdownClasses}
                    href={`${process.env.REACT_APP_DESERET_BOOK_BASE_URL}/account/subscriptions`}
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    Manage Subscription
                  </Navbar.OutsideLink>
                  {legalLinksNavbar}
                  <Navbar.Link
                    className={dropdownClasses}
                    to={logoutUrl()}
                    style={{ color: 'red' }}
                  >
                    Sign out
                  </Navbar.Link>
                </Navbar.Dropdown.Menu>
              </Navbar.Dropdown>
            )}
          </Navbar.End>
        </Container>
      </Navbar>
      {sidebar}
    </header>
  )
}

MainHeader.defaultProps = {
  hasScrollToggle: true,
  isFixed: true,
  className: '',
  navbarProps: {},
}

MainHeader.propTypes = {
  /** if the navbar should hide while scrolling down and display while scrolling up */
  hasScrollToggle: PropTypes.bool,
  /** If the navbar should be fixed to the top of the screen */
  isFixed: PropTypes.bool,
  /** Additional class names. */
  className: PropTypes.string,
  /** Props to pass to the primary `<Navbar />` component */
  navbarProps: PropTypes.shape({}),
}

export default MainHeader
